import React, { useEffect, useMemo, useRef, useState } from "react";

import { EditTwoTone, ExclamationCircleFilled } from "@ant-design/icons";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    Button,
    emitNotification,
    Drawer,
    FormField,
    Tooltip,
} from "@impero-com/ui";
import { rawPalette } from "@impero-com/ui/styles/theme.css";
import { Tabs } from "antd";
import { cx } from "class-variance-authority";
import { useSetAtom } from "jotai";
import { Tab } from "rc-tabs/es/interface";
import { Control, useController, useWatch } from "react-hook-form";
import * as Yup from "yup";

import {
    InitialFieldType,
    SubsequentFieldType,
    DataSheetAssignmentsListDto,
} from "api/generated/model";
import type {
    FieldForSubsequentFieldTypeAndString,
    SavedBlob,
} from "api/generated/model";
import { Field } from "components/fieldBuilder/models";
import { useRHFAdapterAtom } from "org/controlTesting/testingProgram/view/components/ControlConclusionBuilder/useRHFAdapterAtom";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import { ControlViewFormSchema } from "../../formSchema";
import { SuggestAiTasksDrawerContent } from "../SuggestAiTasksDrawerContent";

import * as theme from "./ControlTasksInput.theme.css";
import { getFieldSchema } from "./fieldBuilderUtils";
import { ControlInitialTasks, ControlSubsequentTasks } from "./Tasks";
import { DataSheetAssignmentsForControlProgramAndCurrentControl } from "./types";

type ActiveTab = "responsible" | "firstReviewer" | "secondReviewer";

interface RadioGroupOption {
    label: string;
    value: ActiveTab;
    hasError: boolean;
    fieldBuilderElement: React.ReactNode;
}

interface ControlTasksInputProps {
    control: Control<ControlViewFormSchema>;
    disabled: boolean;
    dataSheetAssignments: DataSheetAssignmentsForControlProgramAndCurrentControl;
    controlId: string;
    blobs: SavedBlob[];
    onAnalytics: (
        suggestionId: string,
        fields: Field<SubsequentFieldType, string>[]
    ) => void;
}

export function ControlTasksInput({
    control,
    disabled,
    dataSheetAssignments,
    controlId,
    blobs,
    onAnalytics,
}: ControlTasksInputProps) {
    const { t } = useTranslation();
    const controlTitle = useWatch({ control, name: "title" });
    const controlProgramId = useWatch({ control, name: "controlProgramId" });
    const description = useWatch({ control, name: "description" });

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<ActiveTab>("responsible");

    // Bridge RHF → atoms for the internal field builders
    const { field: responsibleField } = useController({
        control,
        name: "responsibleTasks",
        disabled,
    });
    const { field: firstReviewerField } = useController({
        control,
        name: "firstReviewerTasks",
        disabled,
    });
    const { field: secondReviewerField } = useController({
        control,
        name: "secondReviewerTasks",
        disabled,
    });

    // Create local FormAtoms that sync back to RHF via useRHFAdapterAtom.
    // Zod validates the full field structure at the schema level; this Yup
    // schema mirrors it to drive per-tab visual errors. A live ref keeps the
    // datasheet existence check fresh once the assignments query resolves.
    // The field casts bridge Zod-inferred types to the Rust-generated Field<> types
    // (structurally identical, but TS can't see that due to branded array types).
    const dataSheetAssignmentsRef =
        useRef<DataSheetAssignmentsListDto>(dataSheetAssignments);
    useEffect(() => {
        dataSheetAssignmentsRef.current = dataSheetAssignments;
    }, [dataSheetAssignments]);
    const fieldSchema = useMemo(
        // eslint-disable-next-line react-hooks/refs
        () => getFieldSchema(dataSheetAssignmentsRef),
        []
    );
    const subsequentSchema = fieldSchema as unknown as Yup.Schema<
        Field<SubsequentFieldType, string>[]
    >;

    const responsibleTasks = responsibleField.value as unknown as Field<
        InitialFieldType,
        string
    >[];
    const responsibleTasksAtom = useRHFAdapterAtom(
        responsibleTasks,
        responsibleField.onChange as unknown as (
            v: Field<InitialFieldType, string>[]
        ) => void,
        fieldSchema
    );
    const firstReviewerTasks = firstReviewerField.value as unknown as Field<
        SubsequentFieldType,
        string
    >[];
    const firstReviewerTasksAtom = useRHFAdapterAtom(
        firstReviewerTasks,
        firstReviewerField.onChange as unknown as (
            v: Field<SubsequentFieldType, string>[]
        ) => void,
        subsequentSchema
    );
    const secondReviewerTasks = secondReviewerField.value as unknown as Field<
        SubsequentFieldType,
        string
    >[];
    const secondReviewerTasksAtom = useRHFAdapterAtom(
        secondReviewerTasks,
        secondReviewerField.onChange as unknown as (
            v: Field<SubsequentFieldType, string>[]
        ) => void,
        subsequentSchema
    );

    const setResponsibleTasks = useSetAtom(responsibleTasksAtom);
    const setFirstReviewerTasks = useSetAtom(firstReviewerTasksAtom);
    const setSecondReviewerTasks = useSetAtom(secondReviewerTasksAtom);

    // All three task arrays share one Tabs widget, so point their refs at a
    // single anchor; whichever tab errors, RHF scrolls here.
    const tasksErrorAnchorRef = (node: HTMLDivElement | null) => {
        responsibleField.ref(node);
        firstReviewerField.ref(node);
        secondReviewerField.ref(node);
    };

    const onAddAISuggestedGroup = (
        fields: FieldForSubsequentFieldTypeAndString[],
        suggestionId: string
    ) => {
        const subsequentFields = fields as Field<SubsequentFieldType, string>[];
        const setterMap: Record<ActiveTab, () => Promise<void>> = {
            responsible: () =>
                setResponsibleTasks((prev) => [
                    ...prev,
                    ...(fields as Field<InitialFieldType, string>[]),
                ]),
            firstReviewer: () =>
                setFirstReviewerTasks((prev) => [...prev, ...subsequentFields]),
            secondReviewer: () =>
                setSecondReviewerTasks((prev) => [
                    ...prev,
                    ...subsequentFields,
                ]),
        };
        emitNotification(
            "success",
            t("ControlTasks_AITasksAddSuccess", { count: fields.length })
        );
        setterMap[activeTab]();
        onAnalytics(suggestionId, subsequentFields);
    };

    return (
        <>
            <ControlTasksTabs
                disabled={disabled}
                fieldRef={tasksErrorAnchorRef}
                dataSheetAssignments={dataSheetAssignments}
                controlId={controlId}
                blobs={blobs}
                controlTitle={controlTitle}
                controlProgramId={controlProgramId}
                description={description}
                activeTab={activeTab}
                onActiveTabChange={setActiveTab}
                onOpenSuggestDrawer={() => setDrawerOpen(true)}
                responsibleTasksAtom={responsibleTasksAtom}
                firstReviewerTasksAtom={firstReviewerTasksAtom}
                secondReviewerTasksAtom={secondReviewerTasksAtom}
            />
            <Drawer
                open={drawerOpen}
                onOpenChange={setDrawerOpen}
                onOpenAutoFocus={(e) => e.preventDefault()}
                variant="ai"
                title={t("ControlTasks_DrawerTitle")}
                infoTooltipText={t("ControlTasks_InfoTooltip")}
                closeAriaLabel={t("ControlTasks_Cancel")}
                betaLabel={t("ControlTasks_Beta")}
            >
                <SuggestAiTasksDrawerContent
                    controlId={controlId}
                    controlTitle={controlTitle}
                    description={description}
                    controlProgramId={controlProgramId}
                    onAddGroup={onAddAISuggestedGroup}
                />
            </Drawer>
        </>
    );
}

interface ControlTasksTabsProps {
    disabled: boolean;
    fieldRef: React.RefCallback<HTMLDivElement>;
    dataSheetAssignments: DataSheetAssignmentsForControlProgramAndCurrentControl;
    controlId: string;
    blobs: SavedBlob[];
    controlTitle: string;
    controlProgramId: ControlViewFormSchema["controlProgramId"];
    description: string;
    activeTab: ActiveTab;
    onActiveTabChange: (tab: ActiveTab) => void;
    onOpenSuggestDrawer: () => void;
    responsibleTasksAtom: FormAtom<Field<InitialFieldType, string>[]>;
    firstReviewerTasksAtom: FormAtom<Field<SubsequentFieldType, string>[]>;
    secondReviewerTasksAtom: FormAtom<Field<SubsequentFieldType, string>[]>;
}

function ControlTasksTabs({
    disabled,
    fieldRef,
    dataSheetAssignments,
    controlId,
    blobs,
    controlTitle,
    controlProgramId,
    description,
    activeTab,
    onActiveTabChange,
    onOpenSuggestDrawer,
    responsibleTasksAtom,
    firstReviewerTasksAtom,
    secondReviewerTasksAtom,
}: ControlTasksTabsProps) {
    const { t } = useTranslation();
    const aiControlTaskSuggestionEnabled = useFeatureFlag(
        "aiControlTaskSuggestion"
    );
    const [tabErrors, setTabErrors] = useState<Record<ActiveTab, boolean>>({
        responsible: false,
        firstReviewer: false,
        secondReviewer: false,
    });

    const validateResponsible = useSetAtom(
        responsibleTasksAtom.validateFieldAtom
    );
    const validateFirstReviewer = useSetAtom(
        firstReviewerTasksAtom.validateFieldAtom
    );
    const validateSecondReviewer = useSetAtom(
        secondReviewerTasksAtom.validateFieldAtom
    );

    const labels: Record<ActiveTab, string> = {
        responsible: t("CreateControl_Tasks_Tab_Responsible"),
        firstReviewer: t("CreateControl_Tasks_Tab_FirstReviewer"),
        secondReviewer: t("CreateControl_Tasks_Tab_SecondReviewer"),
    };

    const handleTabChange = (newActiveTab: string) => {
        onActiveTabChange(newActiveTab as ActiveTab);
        const validateTabMap = {
            responsible: validateResponsible,
            firstReviewer: validateFirstReviewer,
            secondReviewer: validateSecondReviewer,
        };

        Object.entries(validateTabMap).forEach(([tab, validateTab]) => {
            validateTab(true).then((res) => {
                const hasError = res.type === "ERROR";
                setTabErrors((prev) => ({ ...prev, [tab]: hasError }));
            });
        });
    };

    const missingRequiredFields =
        !controlTitle.trim() || !controlProgramId || !description.trim();
    const isNonResponsibleTab = activeTab !== "responsible";
    const suggestButtonDisabled = isNonResponsibleTab || missingRequiredFields;
    const missingFieldsTooltip = missingRequiredFields
        ? t("ControlTasks_SuggestTasks_DisabledTooltip")
        : null;
    const nonResponsibleTabTooltip = isNonResponsibleTab
        ? t("ControlTasks_SuggestTasks_NonResponsibleTabTooltip")
        : null;
    const disabledTooltipLabel =
        nonResponsibleTabTooltip ?? missingFieldsTooltip;
    const suggestButton = (
        <Button
            variant="link"
            size="small"
            icon={
                <EditTwoTone
                    // Needs a proper fix - icon color should/could follow color of the text of the button
                    twoToneColor={
                        suggestButtonDisabled
                            ? rawPalette.base.greys[300]
                            : rawPalette.base.brandEmerald[500]
                    }
                />
            }
            onMouseDown={(e) => e.preventDefault()}
            onClick={onOpenSuggestDrawer}
            disabled={suggestButtonDisabled}
        >
            {t("ControlTasks_SuggestTasks")}
        </Button>
    );

    const suggestButtonWithTooltip = disabledTooltipLabel ? (
        <Tooltip title={disabledTooltipLabel}>{suggestButton}</Tooltip>
    ) : (
        suggestButton
    );

    const headerAction = aiControlTaskSuggestionEnabled
        ? suggestButtonWithTooltip
        : null;
    const options: RadioGroupOption[] = [
        {
            label: labels.responsible,
            value: "responsible",
            hasError: tabErrors.responsible,
            fieldBuilderElement: (
                <ControlInitialTasks
                    disabled={disabled}
                    atom={responsibleTasksAtom}
                    dataSheetAssignments={dataSheetAssignments}
                    controlId={controlId}
                    controlTitle={controlTitle}
                    blobs={blobs}
                    headerAction={headerAction}
                />
            ),
        },
        {
            label: labels.firstReviewer,
            value: "firstReviewer",
            hasError: tabErrors.firstReviewer,
            fieldBuilderElement: (
                <ControlSubsequentTasks
                    disabled={disabled}
                    atom={firstReviewerTasksAtom}
                    dataSheetAssignments={dataSheetAssignments}
                    controlId={controlId}
                    controlTitle={controlTitle}
                    blobs={blobs}
                    headerAction={headerAction}
                />
            ),
        },
        {
            label: labels.secondReviewer,
            value: "secondReviewer",
            hasError: tabErrors.secondReviewer,
            fieldBuilderElement: (
                <ControlSubsequentTasks
                    disabled={disabled}
                    atom={secondReviewerTasksAtom}
                    dataSheetAssignments={dataSheetAssignments}
                    controlId={controlId}
                    controlTitle={controlTitle}
                    blobs={blobs}
                    headerAction={headerAction}
                />
            ),
        },
    ];

    const tabsItems: Tab[] = options.map((option) => {
        const showError = option.hasError && option.value !== activeTab;
        return {
            key: option.value,
            label: (
                <span className={cx(showError && theme.buttonGroupError)}>
                    {showError && <ExclamationCircleFilled />}
                    {option.label}
                </span>
            ),
            children: option.fieldBuilderElement,
        };
    });

    return (
        <FormField fullWidth>
            <div ref={fieldRef} tabIndex={-1} className={theme.columnGap}>
                <Tabs
                    items={tabsItems}
                    onChange={handleTabChange}
                    type="card"
                />
            </div>
        </FormField>
    );
}
